import type { BadgeProps } from './Badge.types';

const baseClasses =
  'tw:inline-flex tw:items-center tw:rounded-[3px] tw:px-[5px] tw:py-[2px] tw:text-[0.8rem] tw:whitespace-nowrap tw:italic';

// preview and success intentionally share the same green styling right now. Success has no visual identity
// of its own yet, so it borrows preview's rather than introducing a second, near-identical green. They are
// kept as separate variantClasses entries (not a single shared variant) specifically so either can diverge
// later by editing its own line here, without touching any call site that already passed variant="success"
// or variant="preview".
const greenBadgeClasses =
  'tw:bg-sq-badge-preview-bg tw:dark:bg-sq-bg-default-preview-dark tw:text-sq-text-interactive tw:dark:text-sq-text-interactive-dark';

const variantClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
  new: 'tw:bg-sq-warning-bg-color tw:dark:bg-sq-text-dark-warning-color tw:text-sq-text-dark-warning-color tw:dark:text-sq-warning-bg-color',
  preview: greenBadgeClasses,
  success: greenBadgeClasses,
  neutral:
    'tw:bg-sq-badge-neutral-bg tw:dark:bg-sq-badge-neutral-bg-dark/40 tw:border tw:border-transparent tw:text-sq-gray-text',
  error: 'tw:bg-sq-badge-error-bg tw:dark:bg-sq-badge-error-bg-dark tw:text-sq-danger',
};

/**
 * Renders a compact inline badge for feature-state labels such as new or preview.
 */
export const Badge = ({ children, variant = 'new', testId, extraClassNames = '' }: BadgeProps) => {
  const appliedClasses = `${baseClasses} ${variantClasses[variant]} ${extraClassNames}`.trim();

  return (
    <span data-testid={testId} className={appliedClasses}>
      {children}
    </span>
  );
};

export default Badge;
